import { IPublicClientApplication } from '@azure/msal-browser';
import type {
  OrgSettings, UserSettings, ReviewSession, SessionDetail, PushResult, TestCase, SimilarCatalogItem,
  UserAccess, UserRole, UserAccessStatus, Project, ProjectStatus,
  ProjectSource, ContextSummary, ContextSummaryPreviewResult, ContextVersion, AutomationJob,
  Release, JiraFixVersion, AssembleResult,
} from '../types';
import { cachedFetch, invalidateCache, clearCache } from './dataCache';

// ── Multi-provider LLM routing ────────────────────────────────────────────────

type LlmConfig = Pick<OrgSettings, 'llmProvider' | 'llmModel' | 'llmApiKey' | 'llmAzureEndpoint'>;

async function callLlm(
  prompt: string,
  config: LlmConfig,
  azureFn: (messages: unknown[]) => Promise<unknown>,
  image?: { base64: string; mediaType: string }
): Promise<string> {
  const { llmProvider: p, llmModel: m, llmApiKey: k, llmAzureEndpoint: azEp } = config;

  if (p && k) {
    if (p === 'claude') {
      const content: unknown = image
        ? [{ type: 'image', source: { type: 'base64', media_type: image.mediaType, data: image.base64 } }, { type: 'text', text: prompt }]
        : prompt;
      const r = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'x-api-key': k, 'anthropic-version': '2023-06-01', 'content-type': 'application/json' },
        body: JSON.stringify({ model: m || 'claude-sonnet-4-6', max_tokens: 8000, messages: [{ role: 'user', content }] }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({} as Record<string, unknown>)); throw new Error((d.error as any)?.message || `Claude API ${r.status}`); }
      return (((await r.json()) as any).content[0].text as string).trim();
    }

    if (p === 'openai' || p === 'groq') {
      const ep = p === 'groq'
        ? 'https://api.groq.com/openai/v1/chat/completions'
        : 'https://api.openai.com/v1/chat/completions';
      const content: unknown[] = image
        ? [{ type: 'image_url', image_url: { url: `data:${image.mediaType};base64,${image.base64}` } }, { type: 'text', text: prompt }]
        : [{ type: 'text', text: prompt }];
      const r = await fetch(ep, {
        method: 'POST',
        headers: { Authorization: `Bearer ${k}`, 'content-type': 'application/json' },
        body: JSON.stringify({ model: m || (p === 'groq' ? 'llama-3.3-70b-versatile' : 'gpt-4o'), max_tokens: 8000, messages: [{ role: 'user', content }] }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({} as Record<string, unknown>)); throw new Error((d.error as any)?.message || `${p} API ${r.status}`); }
      return (((await r.json()) as any).choices[0].message.content as string).trim();
    }

    if (p === 'azureopenai' && azEp && m) {
      const url = `${azEp.replace(/\/$/, '')}/openai/deployments/${m}/chat/completions?api-version=2024-02-01`;
      const content: unknown[] = image
        ? [{ type: 'image_url', image_url: { url: `data:${image.mediaType};base64,${image.base64}` } }, { type: 'text', text: prompt }]
        : [{ type: 'text', text: prompt }];
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'api-key': k, 'content-type': 'application/json' },
        body: JSON.stringify({ max_tokens: 8000, messages: [{ role: 'user', content }] }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({} as Record<string, unknown>)); throw new Error((d.error as any)?.message || `Azure OpenAI ${r.status}`); }
      return (((await r.json()) as any).choices[0].message.content as string).trim();
    }
  }

  // Fallback: Azure Function backend (managed Claude key)
  const claudeContent: unknown = image
    ? [{ type: 'image', source: { type: 'base64', media_type: image.mediaType, data: image.base64 } }, { type: 'text', text: prompt }]
    : prompt;
  const raw = await azureFn([{ role: 'user', content: claudeContent }]);
  const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
  return ((parsed as any).content[0].text as string).trim();
}

const DEV_BYPASS = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

// Token supplied by the Chrome extension bridge (bypasses MSAL when set)
let _bridgeToken: string | null = null;
let _bridgeOid:   string        = '';
let _bridgeEmail: string        = '';

export function setBridgeToken(token: string): void {
  const previousOid = _bridgeOid;
  _bridgeToken = token;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    _bridgeOid   = payload.oid ?? '';
    _bridgeEmail = (payload.preferred_username ?? payload.email ?? payload.upn ?? '').toLowerCase();
  } catch { _bridgeOid = ''; _bridgeEmail = ''; }
  // Guardrail: a token swap that changes identity must never leave the previous
  // user's cached /sessions or /org/users reads reachable by the new one.
  if (_bridgeOid !== previousOid) clearCache();
}

export function getBridgeOid():   string { return _bridgeOid; }
export function getBridgeEmail(): string { return _bridgeEmail; }

// Identity key used to scope the shared read cache (dataCache.ts) so a cached
// response for one signed-in user can never be served to another, even within
// the same browser tab/module lifetime.
function currentIdentityKey(msalInstance: IPublicClientApplication): string {
  if (DEV_BYPASS) return 'dev';
  if (_bridgeToken) return _bridgeOid || _bridgeEmail || 'bridge';
  const account = msalInstance.getAllAccounts()[0];
  return account?.homeAccountId ?? account?.username ?? 'anonymous';
}

// Call on sign-out so any cached reads are dropped immediately, ahead of the
// identity-key check above (which only catches the *next* bridge handshake).
export function clearPortalApiCache(): void {
  clearCache();
}

// Acquires a token silently and falls back to popup
async function getToken(msalInstance: IPublicClientApplication, clientId: string): Promise<string> {
  if (DEV_BYPASS) return 'dev-mock-token';
  if (_bridgeToken) return _bridgeToken;
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) throw new Error('Not authenticated');

  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: [`api://${clientId}/Portal.Access`],
      account: accounts[0],
    });
    return result.accessToken;
  } catch {
    const result = await msalInstance.acquireTokenPopup({
      scopes: [`api://${clientId}/Portal.Access`],
    });
    return result.accessToken;
  }
}

async function apiFetch<T>(
  path: string,
  msalInstance: IPublicClientApplication,
  clientId: string,
  apiBaseUrl: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getToken(msalInstance, clientId);
  // Pass the user's email from the MSAL ID token as a hint header.
  // Azure AD access tokens for api:// scopes don't include preferred_username by default,
  // so the backend uses this hint to match invite records when the JWT email claim is absent.
  const emailHint = !DEV_BYPASS && !_bridgeToken
    ? (msalInstance.getAllAccounts()[0]?.username ?? '')
    : '';
  // FormData bodies (project-source document upload) need the browser to set its own
  // multipart Content-Type with boundary — forcing application/json would break parsing.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const res = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      Authorization: `Bearer ${token}`,
      ...(emailHint ? { 'X-User-Email': emailHint } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(`API ${res.status}: ${msg}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// Finds the text block in an Anthropic-format response, skipping thinking blocks
// that Opus 4.8 may prepend when adaptive thinking is enabled.
function extractTextFromResponse(parsed: any): string {
  const content: any[] = parsed?.content ?? [];
  const block = content.find((c: any) => c.type === 'text');
  if (!block?.text) throw new Error('AI returned no text content. The model may have refused or the response format was unexpected.');
  return (block.text as string).trim();
}

// Expose token acquisition so portal pages can forward the token to the extension
export async function acquirePortalToken(
  msalInstance: IPublicClientApplication,
  clientId: string,
): Promise<string | null> {
  try { return await getToken(msalInstance, clientId); } catch { return null; }
}

// Factory — bind msal + config once, return typed method object
export function createPortalApi(
  msalInstance: IPublicClientApplication,
  clientId: string,
  apiBaseUrl: string,
  llmConfig: LlmConfig = {}
) {
  const f = <T>(path: string, options?: RequestInit) =>
    apiFetch<T>(path, msalInstance, clientId, apiBaseUrl, options);
  const identity = () => currentIdentityKey(msalInstance);

  return {
    getSessions: (mine?: boolean) =>
      cachedFetch(`sessions:${identity()}:${mine === true ? 'mine' : 'all'}`, () =>
        f<ReviewSession[]>(mine === true ? '/sessions?mine=true' : '/sessions')
      ),

    getSession: (id: string) =>
      f<SessionDetail>(`/sessions/${id}`),

    createSession: (payload: {
      title: string; outputType: string; cases: TestCase[]; comment?: string; tags?: string[];
      projectId?: string | null; contextVersionId?: string | null;
    }) =>
      f<{ sessionId: string; portalUrl: string }>('/sessions', {
        method: 'POST',
        body: JSON.stringify(payload),
      }).then(r => { invalidateCache('sessions:'); return r; }),

    patchCase: (sessionId: string, caseId: string, payload: {
      decision: 'approved' | 'rejected' | 'edited';
      editedCase?: Partial<TestCase>;
      rejectionReason?: string;
    }) =>
      f<void>(`/sessions/${sessionId}/cases/${encodeURIComponent(caseId)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),

    getSessionStatus: (id: string) =>
      f<{ total: number; approved: number; rejected: number; pending: number }>(`/sessions/${id}/status`),

    pushSession: (id: string) =>
      f<PushResult[]>(`/sessions/${id}/push`, { method: 'POST' })
        .then(r => { invalidateCache('sessions:'); return r; }),

    automateSession: (id: string) =>
      f<void>(`/sessions/${id}/automate`, { method: 'POST' })
        .then(r => { invalidateCache('sessions:'); return r; }),

    startMcpGeneration: (sessionId: string, caseId: string, targetUrl: string) =>
      f<{ jobId: string }>(`/sessions/${sessionId}/cases/${encodeURIComponent(caseId)}/automation/mcp-generate`, {
        method: 'POST',
        body: JSON.stringify({ targetUrl }),
      }),

    getAutomationJob: (jobId: string) =>
      f<AutomationJob>(`/automation/jobs/${encodeURIComponent(jobId)}`),

    updateSession: (id: string, payload: { title?: string; outputType?: string; assignedReviewers?: string[] }) =>
      f<void>(`/sessions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }).then(r => { invalidateCache('sessions:'); return r; }),

    deleteSession: (id: string) =>
      f<void>(`/sessions/${id}`, { method: 'DELETE' })
        .then(r => { invalidateCache('sessions:'); return r; }),

    deleteCase: (sessionId: string, caseId: string) =>
      f<void>(`/sessions/${sessionId}/cases/${encodeURIComponent(caseId)}`, { method: 'DELETE' }),

    getSettings: () =>
      f<OrgSettings>('/settings'),

    saveSettings: (settings: OrgSettings) =>
      f<void>('/settings', {
        method: 'PUT',
        body: JSON.stringify(settings),
      }),

    getUserSettings: () =>
      f<UserSettings>('/user/settings'),

    saveUserSettings: (settings: UserSettings) =>
      f<void>('/user/settings', {
        method: 'PUT',
        body: JSON.stringify(settings),
      }),

    fetchJiraIssue: (issueId: string) =>
      f<Record<string, unknown>>(`/jira/issue/${encodeURIComponent(issueId)}`),

    getSimilar: (q: string, top = 5): Promise<SimilarCatalogItem[]> =>
      f<SimilarCatalogItem[]>(`/catalog/similar?q=${encodeURIComponent(q)}&top=${top}`)
        .catch(() => []),

    // ── Access / admin ────────────────────────────────────────────────────────

    getMe: () =>
      f<UserAccess>('/me'),

    requestAccess: () =>
      f<UserAccess>('/me', { method: 'POST' }),

    inviteUser: (email: string, role: UserRole) =>
      f<UserAccess>('/org/members', {
        method: 'POST',
        body: JSON.stringify({ email, role }),
      }).then(r => { invalidateCache('orgUsers'); return r; }),

    deleteOrgMember: (userId: string) =>
      f<void>(`/org/members/${encodeURIComponent(userId)}`, { method: 'DELETE' })
        .then(r => { invalidateCache('orgUsers'); return r; }),

    getOrgUsers: () =>
      cachedFetch(`orgUsers:${identity()}`, () => f<UserAccess[]>('/org/users')),

    getAdminUsers: () =>
      f<UserAccess[]>('/org/members'),

    patchAdminUser: (userId: string, payload: { role?: UserRole; status?: UserAccessStatus }) =>
      f<UserAccess>(`/org/members/${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }).then(r => { invalidateCache('orgUsers'); return r; }),

    // ── Projects (product/project context) ────────────────────────────────────

    getProjects: (includeArchived?: boolean) =>
      f<Project[]>(includeArchived ? '/projects?includeArchived=true' : '/projects'),

    // ── Releases (AUTO-301: release-scope detection) ────────────────────

    getJiraFixVersions: () =>
      f<JiraFixVersion[]>('/releases/jira-fix-versions'),

    getReleases: () =>
      f<Release[]>('/releases'),

    getRelease: (id: string) =>
      f<Release>(`/releases/${encodeURIComponent(id)}`),

    createRelease: (payload: { name: string; jiraFixVersion?: string; issueKeys?: string[]; epicKeys?: string[] }) =>
      f<Release>('/releases', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    updateReleaseCandidates: (id: string, included: Record<string, boolean>) =>
      f<Release>(`/releases/${encodeURIComponent(id)}/candidates`, {
        method: 'PATCH',
        body: JSON.stringify({ included }),
      }),

    lockRelease: (id: string) =>
      f<Release>(`/releases/${encodeURIComponent(id)}/lock`, {
        method: 'POST',
      }),

    assembleRelease: (id: string) =>
      f<AssembleResult>(`/releases/${encodeURIComponent(id)}/assemble`, {
        method: 'POST',
      }),

    triggerImpactAnalysis: (id: string) =>
      f<Release>(`/releases/${encodeURIComponent(id)}/impact-analysis`, {
        method: 'POST',
      }),

    applyImpactAnalysis: (id: string, catalogIds: string[]) =>
      f<Release>(`/releases/${encodeURIComponent(id)}/impact-analysis/apply`, {
        method: 'POST',
        body: JSON.stringify({ catalogIds }),
      }),

    getProject: (id: string) =>
      f<Project>(`/projects/${encodeURIComponent(id)}`),

    createProject: (payload: { name: string; domain?: string; description?: string }) =>
      f<Project>('/projects', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    updateProject: (id: string, payload: { name?: string; domain?: string; description?: string; status?: ProjectStatus }) =>
      f<Project>(`/projects/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),

    // ── Project context: sources, summary lifecycle, versions ─────────────────

    getProjectSources: (projectId: string) =>
      f<ProjectSource[]>(`/projects/${encodeURIComponent(projectId)}/sources`),

    createTextSource: (projectId: string, payload: { displayName?: string; text: string }) =>
      f<ProjectSource>(`/projects/${encodeURIComponent(projectId)}/sources`, {
        method: 'POST',
        body: JSON.stringify({ sourceType: 'text', ...payload }),
      }),

    createUrlSource: (projectId: string, payload: { displayName?: string; url: string; cookieHeader?: string }) =>
      f<ProjectSource>(`/projects/${encodeURIComponent(projectId)}/sources`, {
        method: 'POST',
        body: JSON.stringify({ sourceType: 'url', ...payload }),
      }),

    // Document sources carry a raw file, so this posts multipart/form-data instead of JSON.
    // `extractedText` must already be pulled out client-side (see utils/documentExtractor.ts) —
    // the backend chunks/embeds it directly rather than parsing PDFs/DOCX itself.
    createDocumentSource: (projectId: string, file: File, extractedText: string, displayName?: string) => {
      const form = new FormData();
      form.append('file', file);
      form.append('extractedText', extractedText);
      if (displayName) form.append('displayName', displayName);
      return f<ProjectSource>(`/projects/${encodeURIComponent(projectId)}/sources`, {
        method: 'POST',
        body: form,
      });
    },

    deleteProjectSource: (projectId: string, sourceId: string) =>
      f<void>(`/projects/${encodeURIComponent(projectId)}/sources/${encodeURIComponent(sourceId)}`, { method: 'DELETE' }),

    previewContextSummary: (projectId: string, payload: {
      domain?: string; aboutProduct?: string; usersAndUse?: string; additionalExcerpts?: string[];
    }) =>
      f<ContextSummaryPreviewResult>(`/projects/${encodeURIComponent(projectId)}/context/summary/preview`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    confirmContextSummary: (projectId: string, summaryText: string) =>
      f<ContextSummary>(`/projects/${encodeURIComponent(projectId)}/context/summary/confirm`, {
        method: 'POST',
        body: JSON.stringify({ summaryText }),
      }),

    getContextSummary: (projectId: string) =>
      f<ContextSummary>(`/projects/${encodeURIComponent(projectId)}/context/summary`),

    getContextVersions: (projectId: string) =>
      f<ContextVersion[]>(`/projects/${encodeURIComponent(projectId)}/context/versions`),

    // ── Generation ──────────────────────────────────────────────────────────

    // When a projectId is given, generation is routed entirely through the backend's
    // Context Composer (/ai/generate) instead of the direct-provider/`/AIRequest` paths —
    // context injection only happens server-side, so a personal browser-side LLM key can't
    // be used for context-aware requests. No projectId falls back to today's behavior exactly.
    callAI: async (
      prompt: string, types?: string[], contextOptions?: { projectId?: string | null }
    ): Promise<{ text: string; contextVersionId?: string | null }> => {
      const typeList = types && types.length ? types : ['Positive', 'Negative', 'Edge'];
      const typeCommands: Record<string, string> = {
        Positive: 'Based STRICTLY on the requirements above, generate all Positive Test Cases. Use actual field names and valid inputs.',
        Negative: 'Based STRICTLY on the requirements above, generate all Negative Test Cases. Use invalid, missing, or out-of-spec inputs.',
        Edge:     'Based STRICTLY on the requirements above, generate all Edge Test Cases. Target boundary values and extreme conditions.',
      };
      const projectId = contextOptions?.projectId;
      const results = await Promise.all(typeList.map(async (type) => {
        const typePrompt = `${prompt}\n\n${typeCommands[type] ?? ''}`;
        if (projectId) {
          const raw = await f<unknown>('/ai/generate', {
            method: 'POST',
            body: JSON.stringify({ projectId, taskPrompt: typePrompt, queryText: prompt, maxTokens: 8000 }),
          });
          const parsed = (typeof raw === 'string' ? JSON.parse(raw) : raw) as any;
          return { text: extractTextFromResponse(parsed), contextVersionId: parsed?.contextVersionId as string | null | undefined };
        } else {
          const text = await callLlm(typePrompt, llmConfig, (messages) =>
            f<unknown>('/AIRequest', {
              method: 'POST',
              body: JSON.stringify({ model: 'claude-sonnet-4-6', max_tokens: 8000, messages }),
            })
          );
          return { text, contextVersionId: undefined as string | null | undefined };
        }
      }));
      const fullText = results.map(r => r.text).join('\n\n');
      const contextVersionId = results.reduce<string | null | undefined>(
        (acc, r) => r.contextVersionId ?? acc, undefined
      );
      return { text: fullText.trim(), contextVersionId };
    },

    // Sends prompt + optional images (PDF pages, Word embeds, standalone images) in one message.
    callAIWithContent: async (
      prompt: string, images: { base64: string; mediaType: string }[], types?: string[],
      contextOptions?: { projectId?: string | null }
    ): Promise<{ text: string; contextVersionId?: string | null }> => {
      const typeList = types && types.length ? types : ['Positive', 'Negative', 'Edge'];
      const typeCommands: Record<string, string> = {
        Positive: images.length
          ? 'Based STRICTLY on the content above, generate all Positive Test Cases. Use actual field names visible.'
          : 'Based STRICTLY on the requirements above, generate all Positive Test Cases. Use actual field names and valid inputs.',
        Negative: images.length
          ? 'Based STRICTLY on the content above, generate all Negative Test Cases.'
          : 'Based STRICTLY on the requirements above, generate all Negative Test Cases. Use invalid, missing, or out-of-spec inputs.',
        Edge: images.length
          ? 'Based STRICTLY on the content above, generate all Edge Test Cases. Target boundary values.'
          : 'Based STRICTLY on the requirements above, generate all Edge Test Cases. Target boundary values and extreme conditions.',
      };
      const projectId = contextOptions?.projectId;
      const results = await Promise.all(typeList.map(async (type) => {
        const typePrompt = `${prompt}\n\n${typeCommands[type]}`;
        if (projectId) {
          const raw = await f<unknown>('/ai/generate', {
            method: 'POST',
            body: JSON.stringify({
              projectId, taskPrompt: typePrompt, queryText: prompt, maxTokens: 8000,
              images: images.length ? images.map(img => ({ base64: img.base64, mediaType: img.mediaType })) : undefined,
            }),
          });
          const parsed = (typeof raw === 'string' ? JSON.parse(raw) : raw) as any;
          return { text: extractTextFromResponse(parsed), contextVersionId: parsed?.contextVersionId as string | null | undefined };
        } else {
          const content = [
            ...images.map(img => ({ type: 'image', source: { type: 'base64', media_type: img.mediaType, data: img.base64 } })),
            { type: 'text', text: typePrompt },
          ];
          const raw = await f<unknown>('/AIRequest', {
            method: 'POST',
            body: JSON.stringify({
              model: 'claude-sonnet-4-6',
              max_tokens: 8000,
              messages: [{ role: 'user', content: images.length ? content : typePrompt }],
            }),
          });
          const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
          return { text: extractTextFromResponse(parsed), contextVersionId: undefined as string | null | undefined };
        }
      }));
      const fullText = results.map(r => r.text).join('\n\n');
      const contextVersionId = results.reduce<string | null | undefined>(
        (acc, r) => r.contextVersionId ?? acc, undefined
      );
      return { text: fullText.trim(), contextVersionId };
    },
  };
}

export type PortalApi = ReturnType<typeof createPortalApi>;
